/* eslint-disable no-console */
import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChildren, QueryList, ElementRef, AfterViewInit } from "@angular/core";
import { UserDetails } from "src/app/data-models/manage-users/user-details.model";
import { UsersRole } from "src/app/data-models/manage-users/user-role.model";
import { sortArrowActiveNameClass, sortDirection, TableHeaderParam } from "src/app/services/users/users-list.constants";

@Component({
  selector: "app-users-table",
  templateUrl: "./users-table.component.html",
  styleUrls: ["./users-table.component.scss"]
})
export class UsersTableComponent implements OnChanges, AfterViewInit {

  @Input() id: string = "";
  @Input() headers: Array<TableHeaderParam> = [];
  @Input() users: Array<UserDetails> = [];
  @Input() iconPath: string;
  @Input() usersRoleList: UsersRole[] = [];
  @Input() showDetails: boolean = true;
  @Input() orderingQuery: string | null = null;

  @Output() sort = new EventEmitter<{ direction: string; key: string }>();
  @Output() details = new EventEmitter<number>();
  @Output() priceFactorEdited = new EventEmitter<{ user: UserDetails; newValue: number }>();

  @ViewChildren("sortingArrow") protected arrows!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren("priceInput") protected priceInputs!: QueryList<ElementRef<HTMLInputElement>>;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["orderingQuery"]) this.applyActiveSortStyles(this.orderingQuery);
  }

  ngAfterViewInit(): void {
    this.applyActiveSortStyles(this.orderingQuery);
    if (this.arrows && this.arrows.changes)
      this.arrows.changes.subscribe(() => this.applyActiveSortStyles(this.orderingQuery));
  }

  public onSort(direction: string, key: string): void {
    const clicked = this.findSortingArrow(direction, key);
    const oppositeDirection = direction === sortDirection.asc ? sortDirection.desc : sortDirection.asc;
    const opposite = this.findSortingArrow(oppositeDirection, key);

    const clickedIsActive = clicked?.nativeElement?.classList?.contains(sortArrowActiveNameClass);

    if (clickedIsActive) clicked.nativeElement.classList.remove(sortArrowActiveNameClass);
    else {
      clicked?.nativeElement.classList.add(sortArrowActiveNameClass);
      opposite?.nativeElement.classList.remove(sortArrowActiveNameClass);
    }

    this.sort.emit({ direction, key });
  }

  
  private applyActiveSortStyles(orderingQueryParams: string | null): void {
    if (this.arrows) this.arrows.forEach((arrow) => arrow.nativeElement.classList.remove(sortArrowActiveNameClass));

    if (!orderingQueryParams) return;

    const params = orderingQueryParams.split(",");
    params.forEach((param: string) => {
      if (!param) return;
      if (param[0] === "-") {
        const el = this.findSortingArrow(sortDirection.desc, param.substring(1));
        el?.nativeElement.classList.add(sortArrowActiveNameClass);
      } else {
        const el = this.findSortingArrow(sortDirection.asc, param);
        el?.nativeElement.classList.add(sortArrowActiveNameClass);
      }
    });
  }

  private findSortingArrow(direction: string, sortBy: string): ElementRef<HTMLElement> | undefined {
    if (!this.arrows) return undefined;
    return this.arrows.find((arrow: ElementRef<HTMLElement>) => arrow.nativeElement.id === `${direction}-${sortBy}`);
  }


  public onDetails(userId: number): void {
    this.details.emit(userId);
  }  

  public onPriceFactorPaste(event: ClipboardEvent): void {
    const paste = event.clipboardData?.getData("text") || "";
    const parsed = this.parsePriceInput(paste);
    if (!parsed.ok || parsed.cleaned !== paste) {
      event.preventDefault();
      const target = event.target as HTMLInputElement;
      const start = target.selectionStart || 0;
      const end = target.selectionEnd || 0;
      const newValue = target.value.substring(0, start) + parsed.cleaned + target.value.substring(end);
      target.value = newValue;
    }
  }

  public emitPriceFactorEdited(value: string, user: UserDetails): void {
    const parsed = this.parsePriceInput(String(value ?? ""));
    if (!parsed.ok || parsed.value === undefined) {
      this.setInputValueForUser(user.id, user.priceFactor as unknown as number);
      return;
    }

    this.priceFactorEdited.emit({ user, newValue: parsed.value });
  }

  private parsePriceInput(raw: string): { ok: boolean; cleaned: string; value?: number } {
    const s = String(raw ?? "").trim();
    if (s === "") return { ok: false, cleaned: "" };

    const normalized = s.replace(/,/g, ".");

    if (normalized.indexOf("+") !== -1 || normalized.startsWith("-")) {
      const sanitized = normalized.replace(/[+-]/g, "");
      return { ok: false, cleaned: sanitized };
    }

    const cleaned = normalized.replace(/[^0-9.]/g, "");

    if ((cleaned.match(/\./g) || []).length > 1) return { ok: false, cleaned };

    const num = Number(cleaned);
    if (!Number.isFinite(num)) return { ok: false, cleaned };

    const rounded = Math.round((num + Number.EPSILON) * 100) / 100;
    return { ok: true, cleaned: String(rounded), value: rounded };
  }

  public blockPlusMinus(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const key = event.key;
    const code = event.code;
    if (key === "+" || key === "-" || code === "NumpadAdd" || code === "NumpadSubtract")
      event.preventDefault();
  }
  
  public setInputValueForUser(userId: number, value: number): void {
    const el = this.priceInputs.find((p) => p.nativeElement.getAttribute("data-user-id") === String(userId));
    if (el) el.nativeElement.value = value + "";
  }
}
